<!--
  StatusSelect.vue

  Form-context input for task_status / review_status — the standalone peer to
  the embedded StatusSelectField, registered as its `form` slot (parallel to
  ReferenceInput / ValueSelect / SegmentedSelect).

  StatusSelectField shows EITHER its display OR its option list, swapping on
  `mode`, and expects a host to own the overlay. Here that host is us:
    - a persistent trigger instance in `active` (display) that always holds its
      place — its leading icon still advances the status in place (cycling per
      the type's sequence); label / chevron open the list.
    - the option list, a SECOND instance in `editing`, teleported to <body> and
      positioned with floating-ui (offset/flip/shift). Teleporting is what lets
      it escape `overflow` ancestors — e.g. FieldsBlock scroll-clips an inline
      absolute menu — the same approach ToolbarDropdown / GridCell use.

  Read-only collapses to a static icon: StatusSelectField has no readonly prop
  and keys editability off field.isEditable, so force that off for the trigger
  when the form is read-only (and never open).
-->
<template>
  <div
    ref="trigger"
    v-click-outside="onClickOutside"
    class="status-select"
    :class="[textClass, `is-${size}`, { 'is-bordered': bordered }]"
    @click.stop="open = editable"
  >
    <StatusSelectField
      :model-value="modelValue"
      :field="triggerField"
      :size="size"
      mode="active"
      @update:model-value="onUpdate"
    />

    <Teleport to="body">
      <div
        v-if="open && editable"
        ref="menu"
        class="status-select-popover dropdown-content"
        :style="floatingStyles"
      >
        <StatusSelectField
          :model-value="modelValue"
          :field="field"
          :size="size"
          mode="editing"
          @update:model-value="onUpdate"
          @commit="close"
          @cancel="close"
        />
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue"
import { autoUpdate, flip, offset, shift, useFloating } from "@floating-ui/vue"
import type { Field } from "@auger/core/fields"
import { type FieldSize, textSizeClass } from "../useField"
import StatusSelectField from "./StatusSelectField.vue"

// Driven by modelValue + field alone. The form host passes the whole
// field-renderer prop set — item, placeholder, a `changed` listener — that this
// control doesn't use; keep them off the root rather than declaring dead props.
defineOptions({ inheritAttrs: false })

const props = withDefaults(
  defineProps<{
    modelValue: unknown
    field: Field
    readonly?: boolean
    /** Draw as a self-contained control: a border at rest and a standing
     *  chevron, for a host that stands it beside buttons. Off by default, where
     *  the control is a value in a form that grows an edge when pointed at. */
    bordered?: boolean
    size?: FieldSize
  }>(),
  { readonly: false, bordered: false, size: "normal" }
)

const emit = defineEmits<{ "update:modelValue": [value: unknown] }>()

const trigger = ref<HTMLElement | null>(null)
const menu = ref<HTMLElement | null>(null)
const open = ref(false)

// `size` on the ROOT, not just on the field inside it. The root's padding is
// `calc(0.375em - 1px)`, so without a font-size of its own it scaled against
// whatever the host happened to inherit — the control got taller or shorter with
// its surroundings while ignoring its own `size`. Same pattern as AvatarSelect,
// the toolbar control it stands beside.
const textClass = computed(() => textSizeClass(props.size))

const editable = computed(() => !props.readonly && !!props.field.isEditable)

// StatusSelectField has no readonly prop; it keys editability off
// field.isEditable, so force it off for the trigger when the form is read-only.
const triggerField = computed<Field>(() =>
  editable.value ? props.field : { ...props.field, isEditable: false }
)

// Position the teleported menu against the trigger, staying on-screen at edges
// and repositioning live on scroll/resize (same middleware as ToolbarDropdown).
const { floatingStyles } = useFloating(trigger, menu, {
  placement: "bottom-start",
  middleware: [offset(4), flip(), shift({ padding: 8 })],
  whileElementsMounted: autoUpdate,
})

function close() {
  open.value = false
}
function onUpdate(value: unknown) {
  emit("update:modelValue", value)
}

// The menu is teleported out of the trigger, so a click inside it isn't "inside"
// for v-click-outside — ignore those; anything genuinely outside closes. (The
// opening click is `.stop`ped before it reaches the body handler, and option
// clicks stop at the option, so neither self-closes.)
function onClickOutside(event: MouseEvent) {
  if (menu.value && event.composedPath().includes(menu.value)) return
  open.value = false
}

// Focus the option list on open so arrow-keys / Enter work.
watch(open, (isOpen) => {
  if (!isOpen) return
  nextTick(() => menu.value?.querySelector<HTMLElement>(".status-select-menu")?.focus())
})
</script>

<style>
.status-select {
  position: relative;
  display: inline-flex;
  border: 1px solid transparent;
  border-radius: var(--auger-radius);
  padding: var(--auger-control-padding-y);

  &:hover {
    cursor: pointer;
    border-color: var(--auger-border);
  }

  /* Small stands at the shared small-control height rather than at whatever its
     icon and label happen to measure, so it lines up with the inputs it sits
     among in a form. The vertical padding goes with it: the height is stated
     now, and padding on top of it would only push past it. */
  &.is-small {
    align-items: center;
    min-height: var(--auger-control-height-small);
    padding-block: 0;
  }

  /* StatusSelectField's icon-only container query is for narrow grid columns; a
     form has none, so opt out and keep the label. */
  .status-select-field {
    container-type: normal;
  }

  /* `bordered`: a control in its own right, for a host that stands it in a row
     beside buttons, where an edge that only appears on hover reads as a gap.
     Off by default, so every existing form keeps the quiet value-that-grows-an-
     edge behaviour. */
  &.is-bordered {
    align-items: center;
    padding-inline: calc(var(--auger-control-padding-x) - 1px);
    border-color: var(--auger-border);
    background: var(--auger-bg);

    &:hover {
      border-color: var(--auger-border-hover);
    }

    /* The chevron is the cue that this opens a menu, so it stands rather than
       waiting for a hover. Spelled to the full depth StatusSelectField hides it
       at (`.status-select-field .status-select-button .ss-chev`, three classes)
       — a bare `.ss-chev` here is two and silently loses. */
    .status-select-field .status-select-button .ss-chev {
      opacity: 1;
    }
  }
}

/* Teleported to <body>, so styled at the top level (not nested under
   .status-select). floating-ui supplies position/top/left via :style. */
.status-select-popover {
  /* Above an expanded block (900), which is on <body> too. */
  z-index: 950;
  min-width: 11rem;
  padding: 4px;
  border-radius: var(--auger-radius);
  background: var(--auger-bg);
  border: 1px solid var(--auger-border);
  box-shadow: var(--auger-shadow-3);
}

/* On a touch screen this menu is a primary target, not a hover-accurate one.
   The options are drawn for a dense grid cell and land at 24px, which is well
   under a fingertip; they stand at the height of the control that opens them
   instead, and the menu's own width carries the rest of the target. */
@media (pointer: coarse) {
  .status-select-popover .ss-option {
    min-height: 34px;
  }
}
</style>
